import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { getContent } from '../content/siteContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewContact = () => {
  const { language } = useLanguage();
  const content = getContent(language);
  const { contact } = content;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: '',
    description: '',
    timeline: '',
    budget: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company || null,
          project_type: formData.projectType,
          description: formData.description,
          timeline: formData.timeline || null,
          budget: formData.budget || null,
        }),
      });

      if (response.ok) {
        toast.success(
          language === 'es' ? 'Consulta Enviada!' : 'Inquiry Submitted!',
          {
            description: language === 'es' 
              ? 'Gracias por contactarnos. Responderemos en 48 horas.'
              : "Thank you for reaching out. We'll respond within 48 hours.",
          }
        );
        setFormData({
          name: '',
          email: '',
          company: '',
          projectType: '',
          description: '',
          timeline: '',
          budget: '',
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast.error(
        language === 'es' ? 'Error al enviar' : 'Submission Failed',
        {
          description: language === 'es'
            ? 'Por favor intenta de nuevo más tarde.'
            : 'Please try again later.',
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectOptions = language === 'es' 
    ? contact.form.projectOptions 
    : ['Event/Experience', 'Catering', 'Brand Collaboration', 'Operational Consulting', 'Partnership Opportunity', 'Other'];

  return (
    <div className="bg-stone-50 pt-32">
      {/* Hero */}
      <section className="section-padding px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div className="text-center md:text-left">
              <h1 className="font-serif text-5xl md:text-6xl font-bold text-stone-900 mb-6">
                {contact.hero.headline}
              </h1>
              <p className="text-xl md:text-2xl text-stone-600">
                {contact.hero.subheadline}
              </p>
            </div>
            {/* Photo Placeholder */}
            <div className="flex justify-center md:justify-end">
              <div 
                className="w-80 h-96 bg-perla/30 rounded-2xl flex items-center justify-center border-2 border-dashed border-perla"
                data-testid="contact-photo-placeholder"
              >
                <span className="text-stone-400 text-sm uppercase tracking-wider">
                  {language === 'es' ? 'Foto' : 'Photo'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="section-padding px-6">
        <div className="max-w-3xl mx-auto">
          <Card className="border-stone-200">
            <CardContent className="p-8 md:p-12">
              <div className="mb-8">
                <h2 className="font-serif text-3xl font-bold text-stone-900 mb-3">
                  {language === 'es' ? 'Inicia una Conversación' : 'Start a Conversation'}
                </h2>
                <p className="text-stone-600 mb-4">
                  {language === 'es'
                    ? 'Ya sea que estés interesada en eventos, catering, colaboraciones de marca o alianzas, hablemos de cómo podemos trabajar juntas.'
                    : "Whether you're interested in events, catering, brand collaborations, or partnerships, let's discuss how we can work together."}
                </p>
                <a 
                  href="/media-kit.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-terracota hover:underline"
                >
                  <span>{language === 'es' ? 'Descargar Media Kit' : 'Download Media Kit'}</span>
                  <span>→</span>
                </a>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-stone-900 uppercase tracking-wider mb-2">
                      {contact.form.name} *
                    </label>
                    <Input
                      required
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-900 uppercase tracking-wider mb-2">
                      {contact.form.email} *
                    </label>
                    <Input
                      type="email"
                      required
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-900 uppercase tracking-wider mb-2">
                    {contact.form.company}
                  </label>
                  <Input
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    data-testid="contact-company"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-900 uppercase tracking-wider mb-2">
                    {contact.form.projectType} *
                  </label>
                  <select
                    required
                    name="projectType"
                    className="w-full border border-stone-200 rounded px-4 py-2 text-sm"
                    value={formData.projectType}
                    onChange={handleChange}
                    data-testid="contact-project-type"
                  >
                    <option value="">{language === 'es' ? 'Seleccionar...' : 'Select...'}</option>
                    {projectOptions.map((opt, idx) => (
                      <option key={idx} value={opt.toLowerCase().replace(/\s+/g, '-')}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-900 uppercase tracking-wider mb-2">
                    {contact.form.description} *
                  </label>
                  <Textarea
                    required
                    rows={6}
                    name="description"
                    placeholder={language === 'es' 
                      ? 'Comparte tu visión, objetivos y cualquier solicitud específica...'
                      : 'Share your vision, goals, and any specific requests...'}
                    value={formData.description}
                    onChange={handleChange}
                    data-testid="contact-description"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-stone-900 uppercase tracking-wider mb-2">
                    {contact.form.timeline}
                  </label>
                  <Input
                    name="timeline"
                    placeholder={contact.form.timelinePlaceholder}
                    value={formData.timeline}
                    onChange={handleChange}
                    data-testid="contact-timeline"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-stone-900 text-white hover:bg-stone-800 py-6 text-lg font-semibold"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {language === 'es' ? 'Enviando...' : 'Submitting...'}
                    </>
                  ) : (
                    contact.form.submit
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Response Time */}
          <div className="mt-8 flex items-center justify-center gap-2 text-stone-600">
            <Clock className="w-5 h-5" />
            <p className="text-sm">{contact.responseTime}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewContact;
